import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, apiBlob, queryString } from "./api";
import { useCurrentAccess } from "./access";
import { useWorkspace } from "./workspace";

interface ArchiveRecord {
  id: string; printimage_id: number | null; document_number: string; customer_code: string;
  print_sequence: number | null; printed_at_source: string; byte_size: number; sha256: string;
  source_instance: string; document_kind: string; source_revision_id: string | null; status: string; filename: string | null;
}
interface PilotScope { legalEntityId: string; operatingUnitId: string; branch: string }

export function DocumentArchivePage() {
  const { legalEntity } = useWorkspace();
  const access = useCurrentAccess();
  const identity = access.data?.email;
  const [customer, setCustomer] = useState("");
  const [document, setDocument] = useState("");
  const [filter, setFilter] = useState<{customer: string; document: string; before?: string}>({customer: "", document: ""});
  const [downloadError, setDownloadError] = useState("");
  const [downloading, setDownloading] = useState<string>();
  const pilot = useQuery({queryKey: ["document-archive-pilot", identity], enabled: !!identity,
    queryFn: () => api<PilotScope>("/api/v1/document-archive/pilot"), retry: false});
  const matchesCompany = !!legalEntity && pilot.data?.legalEntityId === legalEntity.id;
  const records = useQuery({queryKey: ["document-archive-records", identity, legalEntity?.id, filter],
    enabled: !!identity && matchesCompany && pilot.isSuccess,
    queryFn: () => api<ArchiveRecord[]>(`/api/v1/document-archive/catalog${queryString(filter)}`), retry: false});
  function search(event: FormEvent) { event.preventDefault(); setFilter({customer: customer.trim(), document: document.trim()}); }
  async function download(row: ArchiveRecord) {
    setDownloading(row.id); setDownloadError("");
    try {
      const blob = await apiBlob(`/api/v1/document-archive/catalog/${row.id}/file`, "Document could not be retrieved");
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a"); link.href = url;
      link.download = row.filename ?? `EBS-${row.printimage_id}.original`; link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) { setDownloadError(error instanceof Error ? error.message : "Original could not be retrieved"); }
    finally { setDownloading(undefined); }
  }
  if (pilot.isError) return <section className="panel"><h2>Document archive</h2><p role="alert">{pilot.error.message}</p></section>;
  if (!pilot.data) return <section className="panel"><h2>Document archive</h2><p>Checking pilot access…</p></section>;
  if (!matchesCompany) return <section className="panel"><h2>Document archive</h2><p>Select the company configured for the branch 05 archive pilot.</p></section>;
  return <section className="panel">
    <div className="panel-header"><div><h2>Document archive</h2><p>Branch 05 · EBS originals and current RECO One invoices</p></div></div>
    <form onSubmit={search} style={{display: "flex", gap: "1rem", flexWrap: "wrap", padding: "1rem"}}>
      <label>Customer number<input value={customer} maxLength={80} onChange={event => setCustomer(event.target.value)} /></label>
      <label>Invoice number<input value={document} maxLength={100} onChange={event => setDocument(event.target.value)} /></label>
      <button type="submit" className="primary-button">Search</button>
    </form>
    <p style={{padding: "0 1rem"}}>Search uses exact numbers, including leading zeros. EBS print times use the source system clock; sequence values do not establish approval or validity. RECO One copies show their capture time in UTC and remain separate versions.</p>
    {records.isPending && <p role="status">Loading archived documents…</p>}
    {records.isError && <p role="alert">{records.error.message}</p>}
    {downloadError && <p role="alert">{downloadError}</p>}
    {!records.isError && records.data?.length === 0 && <p>No archived documents match this search.</p>}
    {!records.isError && !!records.data?.length && <div className="table-wrap"><table>
      <thead><tr><th>Invoice</th><th>Customer</th><th>Document</th><th>Printed / captured</th><th>Sequence / revision</th><th>File</th></tr></thead>
      <tbody>{records.data.map(row => <tr key={row.id}>
        <td>{row.document_number}</td><td>{row.customer_code}</td>
        <td>{row.source_instance === "reco-ebs" ? "EBS original" : row.document_kind === "POSTING_INVOICE_COPY" ? "RECO One · Posting-time copy" : "RECO One · Generated copy"}</td>
        <td>{row.printed_at_source}</td>
        <td style={{overflowWrap: "anywhere"}}>{row.source_instance === "reco-ebs" ? `${row.print_sequence} · PRINTIMAGE ${row.printimage_id}` : row.source_revision_id}</td>
        <td>{row.status !== "RETAINED" ? <span>Captured · awaiting archive storage</span> : <button type="button" disabled={!!downloading} onClick={() => void download(row)}>{downloading === row.id ? "Retrieving…" : row.source_instance === "reco-ebs" ? "Download original" : "Download PDF"}</button>}</td>
      </tr>)}</tbody>
    </table></div>}
    <div style={{display: "flex", gap: "1rem", padding: "1rem"}}>
      {filter.before !== undefined && <button onClick={() => setFilter({...filter, before: undefined})}>First page</button>}
      {records.data?.length === 100 && <button disabled={records.isFetching} onClick={() => setFilter({...filter, before: records.data?.at(-1)?.id})}>Next page</button>}
    </div>
  </section>;
}
